/*
  Créateur de bijoux à charms — DE NADA PARIS
  Parcours repris du prototype « Atelier Charms » :
  - 01 Chaîne : bracelet ou collier, puis la maille (produits des collections choisies dans la section)
  - 02 Charms : accrochés à la chaîne à des emplacements numérotés, réordonnables
  - 03 Finitions : boîte Bubble Jewelry offerte et note pour l'atelier
  Aperçu en direct : la vraie photo produit de la chaîne choisie, les vraies photos des charms détourées et posées dessus.
  Panier : charms en lignes séparées (stock) + chaîne avec la composition et l'image atelier jointe.
  Arrivée depuis l'accueil : ?chaine=<handle>&charms=<id>,<id> (bloc « À vos charms »).
*/
(() => {
  if (customElements.get('charms-builder')) return;

  // Boutique en français et en anglais : l'atelier s'affiche dans la langue de la page
  // (l'image atelier et les propriétés du panier restent en français, pour l'atelier)
  const EN = (document.documentElement.lang || '').toLowerCase().startsWith('en');
  const tr = (fr, en) => (EN ? en : fr);
  const STEPS = tr(['Chaîne', 'Charms', 'Finitions'], ['Chain', 'Charms', 'Finishing']);
  const TYPE_LABEL = { bracelet: 'Bracelet', collier: 'Collier' };
  const TYPE_EN = { bracelet: 'Bracelet', collier: 'Necklace' };
  const TL = (type) => tr(TYPE_LABEL[type], TYPE_EN[type]);
  const CATS = [
    ['charm-amour', tr('Amour', 'Love')], ['charm-animaux', tr('Animaux', 'Animals')], ['charm-chance', tr('Chance', 'Luck')],
    ['charm-cool-kids', 'Cool Kids'], ['charm-food', 'Food'], ['charm-plage', tr('Plage', 'Beach')], ['charm-voyage', tr('Voyage', 'Travel')]
  ];
  const STORE_KEY = 'denada-charms-builder-v1';

  // Moteur d'aperçu partagé avec le bloc d'accueil (assets/charms-engine.js, chargé avant ce fichier)
  const {
    W, RATIO, LENGTH, CHARM_H, A, BG, esc, shortName, sizeHint, randomRef,
    analyzeChain, fallbackChain, backdrop, strokeGold, drawRing, detour, formatMoney
  } = window.DenadaCharms;

  // Le nom traduit d'une chaîne contient parfois déjà son type (« Paperclip Bracelet ») : on ne le répète pas
  const namesType = (chain) => new RegExp(`\\b(${TYPE_LABEL[chain.type]}|${TYPE_EN[chain.type]})\\b`, 'i').test(shortName(chain.t));
  const chainLabel = (chain) => (namesType(chain) ? shortName(chain.t) : `${TL(chain.type)} ${shortName(chain.t)}`);

  /* ---------- Composant ---------- */

  class CharmsBuilder extends HTMLElement {
    connectedCallback() {
      if (this._ready) return;
      this._ready = true;

      const cfg = JSON.parse(this.querySelector('[data-cb-config]').textContent);
      this.cfg = cfg;
      this.moneyFormat = cfg.moneyFormat || '€{{amount_with_comma_separator}}';
      this.groups = cfg.groups || {};
      this.chains = cfg.chains.filter((c) => c.type === 'bracelet' || c.type === 'collier');
      this.charms = new Map();
      cfg.charms.forEach((c) => {
        if (c.img && !this.charms.has(c.id)) this.charms.set(c.id, c);
      });

      this.$ = (sel) => this.querySelector(sel);
      this.canvas = this.$('[data-cb-canvas]');
      this.canvas.width = W;
      this.ctx = this.canvas.getContext('2d');
      this.images = new Map();
      this.cuts = new Map();
      this.ready = new Map();
      this.photos = new Map();
      this.scene = null;
      this.cartTotal = 0;
      this.uid = 1;
      this.reduced = matchMedia('(prefers-reduced-motion: reduce)').matches;

      this.state = this.load();
      this.bind();
      this.render();
      this.refreshCart();
      document.addEventListener('visibilitychange', () => { if (!document.hidden) this.requestDraw(); });
    }

    /* ---------- État ---------- */

    load() {
      const firstOf = (type) => (this.chains.find((c) => c.type === type && c.ok) || this.chains.find((c) => c.type === type))?.h || null;
      const state = {
        type: this.chains.some((c) => c.type === 'bracelet') ? 'bracelet' : 'collier',
        chain: { bracelet: firstOf('bracelet'), collier: firstOf('collier') },
        items: [],
        note: '',
        step: 0,
        filter: 'all',
        ref: randomRef()
      };
      try {
        const saved = JSON.parse(localStorage.getItem(STORE_KEY) || 'null');
        if (saved && TYPE_LABEL[saved.type]) {
          state.type = saved.type;
          ['bracelet', 'collier'].forEach((t) => {
            if (saved.chain?.[t] && this.chainBy(saved.chain[t])) state.chain[t] = saved.chain[t];
          });
          state.items = (saved.items || []).filter((id) => this.charms.has(id)).map((id) => ({ id, k: this.uid++ }));
          state.note = typeof saved.note === 'string' ? saved.note.slice(0, 200) : '';
          state.step = Math.min(2, Math.max(0, saved.step | 0));
          state.filter = saved.filter || 'all';
          state.ref = saved.ref || state.ref;
        }
      } catch (e) { /* stockage indisponible */ }

      // Arrivée depuis une fiche chaîne : ?chaine=<handle>
      const params = new URLSearchParams(location.search);
      const fromUrl = this.chainBy(params.get('chaine'));
      if (fromUrl) {
        state.type = fromUrl.type;
        state.chain[fromUrl.type] = fromUrl.h;
        state.step = 1;
      } else if (TYPE_LABEL[params.get('type')]) {
        state.type = params.get('type');
      }
      // Charms choisis dans le bloc d'accueil « À vos charms » : &charms=<id>,<id>
      if (params.has('charms')) {
        state.items = params.get('charms').split(',').map(Number)
          .filter((id) => this.charms.has(id))
          .map((id) => ({ id, k: this.uid++ }));
        state.step = 1;
        // retiré de l'adresse : un rechargement garde ensuite les changements faits dans l'atelier
        params.delete('charms');
        const qs = params.toString();
        history.replaceState(history.state, '', `${location.pathname}${qs ? `?${qs}` : ''}${location.hash}`);
      }
      const max = this.cfg.max[state.type];
      if (state.items.length > max) state.items.length = max;
      return state;
    }

    save() {
      const s = this.state;
      try {
        localStorage.setItem(STORE_KEY, JSON.stringify({
          type: s.type, chain: s.chain, items: s.items.map((i) => i.id), note: s.note, step: s.step, filter: s.filter, ref: s.ref
        }));
      } catch (e) { /* stockage indisponible */ }
    }

    chainBy(handle) { return this.chains.find((c) => c.h === handle) || null; }
    get chain() { return this.chainBy(this.state.chain[this.state.type]); }
    get max() { return this.cfg.max[this.state.type] || 15; }
    allowed(charm) {
      const chain = this.chain;
      return charm && (!chain || !chain.charms || chain.charms.includes(charm.id));
    }
    count(id) { return this.state.items.filter((i) => i.id === id).length; }

    /* ---------- Prix ---------- */

    formatMoney(cents) {
      return formatMoney(cents, this.moneyFormat);
    }

    total() {
      const chain = this.chain;
      return (chain ? chain.p : 0) + this.state.items.reduce((s, i) => s + (this.charms.get(i.id)?.p || 0), 0);
    }

    lines() {
      const chain = this.chain, out = [];
      if (chain) out.push({ label: chainLabel(chain), detail: chain.size || tr('Chaîne', 'Chain'), price: chain.p });
      const grouped = new Map();
      this.state.items.forEach((it, i) => {
        if (!grouped.has(it.id)) grouped.set(it.id, []);
        grouped.get(it.id).push(i + 1);
      });
      if (grouped.size > 5) {
        // au-delà de 5 charms différents, une seule ligne pour garder le récapitulatif lisible
        const n = this.state.items.length;
        out.push({
          label: `${n} charms`,
          detail: this.state.items.map((it) => this.charms.get(it.id).t).join(', '),
          price: this.state.items.reduce((sum, it) => sum + this.charms.get(it.id).p, 0)
        });
      } else {
        grouped.forEach((pos, id) => {
          const c = this.charms.get(id);
          out.push({
            label: `${pos.length > 1 ? `${pos.length} × ` : ''}Charm ${c.t}`,
            detail: `Position${pos.length > 1 ? 's' : ''} ${pos.join(', ')}`,
            price: c.p * pos.length
          });
        });
      }
      return out;
    }

    /* ---------- Rendu du panneau ---------- */

    stepStatus(i) {
      const chain = this.chain;
      if (i === 0) return chain ? (namesType(chain) ? shortName(chain.t) : `${shortName(chain.t)} · ${TL(chain.type).toLowerCase()}`) : tr('À choisir', 'To choose');
      if (i === 1) return `${this.state.items.length} / ${this.max}`;
      return tr('Boîte offerte', 'Free box');
    }

    render() {
      const s = this.state;
      this.$('[data-cb-heading]').textContent = tr(`Composez votre ${TYPE_LABEL[s.type].toLowerCase()}`, `Create your ${TYPE_EN[s.type].toLowerCase()}`);
      this.$('[data-cb-steps]').innerHTML = STEPS.map((n, i) => `
        <button class="cb__step" type="button" data-a="step" data-v="${i}" ${s.step === i ? 'aria-current="step"' : ''}>
          <span class="n">${String(i + 1).padStart(2, '0')}</span><span class="t">${n}</span><span class="s">${esc(this.stepStatus(i))}</span>
        </button>`).join('');
      this.$('[data-cb-body]').innerHTML = [this.step0, this.step1, this.step2][s.step].call(this);
      if (s.step === 1) { this.renderSlots(); this.renderGrid(); }
      this.renderBar();
      this.renderReceipt();
      this.renderPills();
      this.updateScene();
    }

    // Mise à jour légère après un ajout ou un retrait (sans recharger la grille)
    refresh() {
      const s = this.state;
      this.$$('[data-cb-steps] .cb__step .s').forEach((el, i) => { el.textContent = this.stepStatus(i); });
      if (s.step === 1) { this.renderSlots(); this.updateGrid(); }
      this.renderBar();
      this.renderReceipt();
      this.updateScene();
    }

    $$(sel) { return [...this.querySelectorAll(sel)]; }

    step0() {
      const s = this.state, chain = this.chain;
      const types = ['bracelet', 'collier'].filter((t) => this.chains.some((c) => c.type === t));
      const list = this.chains.filter((c) => c.type === s.type);
      return `
        <section class="cb__group"><h2 class="cb__label">${tr('Bijou', 'Piece')}</h2>
          <div class="cb__seg">${types.map((t) => `
            <button type="button" data-a="type" data-v="${t}" aria-pressed="${s.type === t}">${TL(t)} <small>${tr(`jusqu'à ${this.cfg.max[t]} charms`, `up to ${this.cfg.max[t]} charms`)}</small></button>`).join('')}
          </div></section>
        <section class="cb__group"><h2 class="cb__label">${tr('Maille', 'Chain style')} <span class="v">${chain ? esc(shortName(chain.t)) : ''}</span></h2>
          <div class="cb__cards">${list.map((c) => `
            <button class="cb__opt" type="button" data-a="chain" data-v="${esc(c.h)}" aria-pressed="${chain && chain.h === c.h}" ${c.ok ? '' : 'disabled'}>
              ${c.img ? `<img src="${esc(c.img)}" alt="" width="300" height="300" loading="lazy">` : ''}
              <span class="nm">${esc(shortName(c.t))}</span>
              <span class="mt">${esc(c.size || '')}</span>
              <span class="pr">${c.ok ? this.formatMoney(c.p) : tr('Épuisé', 'Sold out')}</span>
            </button>`).join('')}
          </div>
          <p class="cb__hint">${tr('Chaîne en acier inoxydable, assemblée à la main avec vos charms dans notre atelier parisien.', 'Stainless steel chain, hand-assembled with your charms in our Paris atelier.')}</p>
        </section>`;
    }

    step1() {
      return `
        <section class="cb__group"><h2 class="cb__label">${tr('Vos charms', 'Your charms')} <span class="v" data-cb-slot-count></span></h2>
          <div class="cb__slots" data-cb-slots></div></section>
        <section class="cb__group" style="margin-bottom:8px"><h2 class="cb__label">Collection</h2>
          <div class="cb__filters" role="group" aria-label="${tr('Filtrer les charms', 'Filter charms')}">${this.filters().map(([id, n]) => `
            <button type="button" class="cb__chip" data-a="filter" data-v="${esc(id)}" aria-pressed="${this.state.filter === id}">${esc(n)}</button>`).join('')}
          </div>
          <div class="cb__grid" data-cb-grid></div>
          <p class="cb__hint" data-cb-full hidden></p>
        </section>`;
    }

    step2() {
      const pack = this.cfg.packaging || {};
      const note = this.state.note;
      return `
        <section class="cb__group"><h2 class="cb__label">${tr('Emballage', 'Packaging')}</h2>
          <div class="cb__pack">
            ${pack.image ? `<img src="${esc(pack.image)}" alt="" width="152" height="152" loading="lazy">` : '<span></span>'}
            <span><span class="nm">${esc(pack.title || 'Emballage')}</span><br><span class="mt">${esc(pack.text || '')}</span></span>
            <span class="pr">${tr('Offerte', 'Free')}</span>
          </div></section>
        <section class="cb__group"><h2 class="cb__label"><label for="cb-note-${this.id}">${tr('Informations complémentaires', 'Additional information')}</label> <span class="v">${tr('facultatif', 'optional')}</span></h2>
          <textarea id="cb-note-${this.id}" data-cb-note maxlength="200" placeholder="${tr('Une précision pour notre atelier ?', 'Anything our atelier should know?')}">${esc(note)}</textarea>
          <div class="cb__count" data-cb-note-count>${note.length} / 200</div></section>
        <p class="cb__hint">${tr('Vérifiez le récapitulatif, puis ajoutez votre création au panier.', 'Check the summary, then add your creation to the cart.')}</p>`;
    }

    filters() {
      const list = [...this.charms.values()].filter((c) => this.allowed(c));
      const out = [['all', tr('Tous', 'All')]];
      Object.entries(this.groups).forEach(([key, label]) => {
        if (list.some((c) => c.g === key)) out.push([`g:${key}`, label.replace(/^charms?\s+/i, '').replace(/^./, (m) => m.toUpperCase())]);
      });
      CATS.forEach(([tag, label]) => { if (list.some((c) => c.tags.includes(tag))) out.push([tag, label]); });
      return out;
    }

    visibleCharms() {
      const f = this.state.filter;
      return [...this.charms.values()].filter((c) => this.allowed(c) && (
        f === 'all' || (f.startsWith('g:') ? c.g === f.slice(2) : c.tags.includes(f))
      ));
    }

    renderSlots() {
      const s = this.state, max = this.max;
      const rows = s.items.map((it, i) => {
        const c = this.charms.get(it.id);
        return `<div class="cb__slot${it.k === this.lastAdded ? ' is-new' : ''}">
          <img src="${esc(c.img)}" alt="" width="96" height="96" loading="lazy" crossorigin="anonymous">
          <div><div class="nm">${esc(c.t)}</div><div class="mt">Position ${i + 1} · ${this.formatMoney(c.p)}</div></div>
          <div class="cb__ctl">
            <button type="button" data-a="left" data-v="${i}" ${i === 0 ? 'disabled' : ''} aria-label="${tr(`Déplacer ${esc(c.t)} vers la gauche`, `Move ${esc(c.t)} left`)}">←</button>
            <button type="button" data-a="right" data-v="${i}" ${i === s.items.length - 1 ? 'disabled' : ''} aria-label="${tr(`Déplacer ${esc(c.t)} vers la droite`, `Move ${esc(c.t)} right`)}">→</button>
            <button type="button" class="rm" data-a="rm" data-v="${i}" aria-label="${tr(`Retirer ${esc(c.t)}`, `Remove ${esc(c.t)}`)}">✕</button>
          </div></div>`;
      }).join('');
      const free = max - s.items.length;
      const empty = free > 0
        ? `<div class="cb__slot empty"><span class="ph">${s.items.length + 1}</span><span>${free === max ? tr(`${max} emplacements libres : choisissez vos charms ci-dessous`, `${max} free spots: choose your charms below`) : tr(`Encore ${free} emplacement${free > 1 ? 's' : ''} libre${free > 1 ? 's' : ''}`, `${free} spot${free > 1 ? 's' : ''} left`)}</span></div>`
        : '';
      this.$('[data-cb-slots]').innerHTML = rows + empty;
      this.$('[data-cb-slot-count]').textContent = tr(`${s.items.length} sur ${max} emplacements`, `${s.items.length} of ${max} spots`);
    }

    renderGrid() {
      const list = this.visibleCharms();
      this.$('[data-cb-grid]').innerHTML = list.map((c) => `
        <div class="cb__card" data-cb-card="${c.id}">
          <img src="${esc(c.img)}" alt="" width="200" height="200" loading="lazy" crossorigin="anonymous">
          <span class="nm">${esc(c.t)}</span>
          <span class="pr">${this.formatMoney(c.p)}</span>
          <button type="button" data-a="add" data-v="${c.id}">${tr('Ajouter', 'Add')}</button>
          <span class="cb__badge" hidden></span>
        </div>`).join('') || `<p class="cb__hint">${tr('Aucun charm disponible dans cette catégorie pour le moment.', 'No charms available in this category right now.')}</p>`;
      this.updateGrid();
    }

    updateGrid() {
      const full = this.state.items.length >= this.max;
      this.$$('[data-cb-card]').forEach((card) => {
        const c = this.charms.get(Number(card.dataset.cbCard));
        const n = this.count(c.id);
        const out = n >= c.q;
        const btn = card.querySelector('button');
        btn.disabled = full || out;
        btn.textContent = full ? tr('Complet', 'Full') : out ? tr('Stock atteint', 'Out of stock') : tr('Ajouter', 'Add');
        btn.setAttribute('aria-label', tr(`Ajouter ${c.t}`, `Add ${c.t}`));
        const badge = card.querySelector('.cb__badge');
        badge.hidden = !n;
        badge.textContent = `×${n}`;
        card.classList.toggle('is-used', n > 0);
      });
      const hint = this.$('[data-cb-full]');
      if (hint) {
        hint.hidden = !full;
        hint.textContent = tr(`Les ${this.max} emplacements sont pris. Retirez un charm pour en ajouter un autre.`, `All ${this.max} spots are taken. Remove a charm to add another.`);
      }
    }

    renderBar() {
      const s = this.state.step, last = s === STEPS.length - 1;
      this.$('[data-cb-bar]').innerHTML = `
        <div class="cb__total"><small>Total</small><strong>${this.formatMoney(this.total())}</strong></div>
        <button class="cb__btn" type="button" data-a="prev" ${s === 0 ? 'disabled' : ''}>${tr('Retour', 'Back')}</button>
        ${last
          ? `<button class="cb__btn primary" type="button" data-a="cart" ${this.busy ? 'aria-busy="true" disabled' : ''}>${this.busy ? tr('Ajout…', 'Adding…') : tr('Ajouter au panier', 'Add to cart')}</button>`
          : `<button class="cb__btn primary" type="button" data-a="next">${STEPS[s + 1]} →</button>`}`;
    }

    renderReceipt() {
      const ls = this.lines(), t = this.total();
      const threshold = this.cfg.freeShipping || 0;
      const left = Math.max(0, threshold - (this.cartTotal + t));
      this.$('[data-cb-receipt]').innerHTML = `
        <h2>${tr('Votre création', 'Your creation')}</h2>
        <ul class="cb__lines">${ls.map((l) => `<li><span>${esc(l.label)}<em>${esc(l.detail)}</em></span><span>${l.price ? this.formatMoney(l.price) : 'Offerte'}</span></li>`).join('')}</ul>
        <div class="cb__sum"><span>Total</span><strong>${this.formatMoney(t)}</strong></div>
        ${threshold ? `<div class="cb__ship">${left
          ? tr(`Plus que ${this.formatMoney(left)} pour la livraison offerte en France métropolitaine.`, `Only ${this.formatMoney(left)} away from free delivery in mainland France.`)
          : tr('Livraison offerte en France métropolitaine.', 'Free delivery in mainland France.')}<div class="track"><div class="fill" style="width:${Math.min(100, ((this.cartTotal + t) / threshold) * 100).toFixed(1)}%"></div></div></div>` : ''}`;
    }

    renderPills() {
      const chain = this.chain;
      this.$('[data-cb-pill]').textContent = chain ? `${shortName(chain.t)}${chain.size ? ` · ${chain.size}` : ''}` : '';
    }

    toast(msg) {
      const t = this.$('[data-cb-toast]');
      t.textContent = msg;
      t.classList.add('show');
      clearTimeout(this.toastT);
      this.toastT = setTimeout(() => t.classList.remove('show'), 3000);
    }

    showError(msg) {
      const el = this.$('[data-cb-error]');
      el.textContent = msg || '';
      el.hidden = !msg;
    }

    /* ---------- Actions ---------- */

    bind() {
      this.addEventListener('click', (e) => {
        const t = e.target.closest('[data-a]');
        if (!t || t.disabled) return;
        const a = t.dataset.a, v = t.dataset.v, s = this.state;
        this.showError('');
        switch (a) {
          case 'step': this.go(Number(v)); break;
          case 'next':
            if (s.step === 0 && !this.chain?.ok) { this.toast(tr('Choisissez une chaîne disponible.', 'Choose an available chain.')); break; }
            if (s.step === 1 && !s.items.length) { this.toast(tr('Ajoutez au moins un charm.', 'Add at least one charm.')); break; }
            this.go(s.step + 1);
            break;
          case 'prev': this.go(s.step - 1); break;
          case 'type': this.setType(v); break;
          case 'chain':
            s.chain[s.type] = v;
            this.trimToChain();
            this.commit(true);
            break;
          case 'filter': s.filter = v; this.save(); this.$$('.cb__chip').forEach((b) => b.setAttribute('aria-pressed', b.dataset.v === v)); this.renderGrid(); break;
          case 'add': this.add(Number(v)); break;
          case 'rm': s.items.splice(Number(v), 1); this.lastAdded = null; this.commit(); break;
          case 'left': case 'right': {
            const i = Number(v), j = a === 'left' ? i - 1 : i + 1;
            if (j < 0 || j >= s.items.length) break;
            [s.items[i], s.items[j]] = [s.items[j], s.items[i]];
            this.lastAdded = s.items[j].k;
            this.commit();
            break;
          }
          case 'reset':
            s.items = [];
            s.note = '';
            s.step = 0;
            s.ref = randomRef();
            this.lastAdded = null;
            this.commit(true);
            this.toast(tr('Nouvelle création : choisissez votre chaîne.', 'New creation: choose your chain.'));
            break;
          case 'cart': this.addToCart(); break;
        }
      });
      this.addEventListener('input', (e) => {
        if (!e.target.matches('[data-cb-note]')) return;
        this.state.note = e.target.value.slice(0, 200);
        this.$('[data-cb-note-count]').textContent = `${this.state.note.length} / 200`;
        this.save();
      });
    }

    go(step) {
      this.state.step = Math.max(0, Math.min(STEPS.length - 1, step));
      this.commit(true);
      const top = this.$('.cb__panel').getBoundingClientRect().top;
      if (top < 0) this.$('.cb__panel').scrollIntoView({ behavior: this.reduced ? 'auto' : 'smooth', block: 'start' });
    }

    commit(full) {
      this.save();
      if (full) this.render(); else this.refresh();
    }

    setType(type) {
      const s = this.state;
      if (!TYPE_LABEL[type]) return;
      s.type = type;
      if (!this.chain) s.chain[type] = (this.chains.find((c) => c.type === type && c.ok) || {}).h || null;
      const max = this.max;
      if (s.items.length > max) {
        const removed = s.items.length - max;
        s.items.length = max;
        this.toast(tr(`${removed} charm${removed > 1 ? 's' : ''} retiré${removed > 1 ? 's' : ''} : un ${TYPE_LABEL[type].toLowerCase()} en accepte ${max}.`, `${removed} charm${removed > 1 ? 's' : ''} removed: a ${TYPE_EN[type].toLowerCase()} holds ${max}.`));
      }
      this.trimToChain();
      this.commit(true);
    }

    trimToChain() {
      const before = this.state.items.length;
      this.state.items = this.state.items.filter((it) => this.allowed(this.charms.get(it.id)));
      const removed = before - this.state.items.length;
      if (removed) this.toast(tr(`${removed} charm${removed > 1 ? 's' : ''} non disponible${removed > 1 ? 's' : ''} sur cette chaîne retiré${removed > 1 ? 's' : ''}.`, `${removed} charm${removed > 1 ? 's' : ''} not available on this chain removed.`));
    }

    add(id) {
      const s = this.state, c = this.charms.get(id);
      if (!c) return;
      if (s.items.length >= this.max) { this.toast(tr(`Un ${TYPE_LABEL[s.type].toLowerCase()} peut porter jusqu'à ${this.max} charms.`, `A ${TYPE_EN[s.type].toLowerCase()} can hold up to ${this.max} charms.`)); return; }
      if (this.count(id) >= c.q) { this.toast(`Plus de ${c.t} en stock.`); return; }
      const item = { id, k: this.uid++ };
      s.items.push(item);
      this.lastAdded = item.k;
      this.dropAt = performance.now();
      this.commit();
      this.toast(tr(`${c.t} ajouté en position ${s.items.length}`, `${c.t} added in position ${s.items.length}`));
    }

    /* ---------- Aperçu ---------- */

    loadImage(url) {
      if (!this.images.has(url)) {
        this.images.set(url, new Promise((resolve, reject) => {
          const img = new Image();
          img.crossOrigin = 'anonymous';
          img.onload = () => resolve(img);
          img.onerror = reject;
          img.src = url;
        }));
      }
      return this.images.get(url);
    }

    cutout(charm) {
      if (!this.cuts.has(charm.id)) {
        // même adresse que la vignette (chargée en CORS) : l'image vient du cache
        this.cuts.set(charm.id, this.loadImage(charm.img).then(detour).catch(() => null).then((cut) => {
          this.ready.set(charm.id, cut);
          return cut;
        }));
      }
      return this.cuts.get(charm.id);
    }

    photoUrl(chain) {
      return chain && chain.img ? chain.img.replace(/([?&])width=\d+/, '$1width=1600') : null;
    }

    // Photo de la chaîne, tracé repéré et fond prolongé (gardés pour les 3 dernières chaînes vues)
    chainPhoto(chain, type) {
      const key = `${type}|${chain ? chain.h : ''}`;
      if (!this.photos.has(key)) {
        const url = this.photoUrl(chain);
        const job = (url ? this.loadImage(url) : Promise.reject(new Error('sans photo')))
          .then((img) => {
            const info = analyzeChain(img);
            return info ? { info, back: backdrop(img) } : null;
          })
          .catch(() => null)
          .then((res) => res || { info: fallbackChain(type), back: null });
        this.photos.set(key, job);
        if (this.photos.size > 3) this.photos.delete(this.photos.keys().next().value);
      }
      return this.photos.get(key);
    }

    // Charms répartis le long du bas de la chaîne, posés vers l'extérieur, à l'échelle de la photo
    layout(type, info, items, cuts) {
      const u = info.total / LENGTH[type]; // unités de la photo par cm
      const sized = items.map((it, i) => {
        const c = this.charms.get(it.id), cut = cuts[i];
        let h = CHARM_H * sizeHint(c.t) * u;
        let w = cut ? (h * cut.w) / cut.h : h * 0.7;
        if (w > 1.9 * u) { h *= (1.9 * u) / w; w = 1.9 * u; }
        // largeur de part et d'autre de l'anneau (pas toujours centré)
        const ax = cut ? cut.ax : 0.5;
        return { it, cut, w, h, ax, left: w * ax + 0.06 * u, right: w * (1 - ax) + 0.06 * u };
      });
      const sum = sized.reduce((a, x) => a + x.left + x.right, 0);
      const usable = info.total * (type === 'bracelet' ? 0.7 : 0.5);
      const k = sum > usable ? usable / sum : 1;
      let s = info.low - (sum * k) / 2;
      const placed = sized.map((x) => {
        s += x.left * k;
        const p = info.at(s);
        s += x.right * k;
        return { ...x, p, ang: Math.atan2(p.y - p.my, p.x - p.mx) - Math.PI / 2 };
      });
      return { placed, u };
    }

    // Cadrage : toute la chaîne (avec fermoir, rallonge et médaille) et les charms, au format de l'aperçu
    cropFor(type, info, placed, u) {
      let minX = Infinity, maxX = -Infinity, minY = Infinity, maxY = -Infinity;
      const grow = (x, y) => {
        minX = Math.min(minX, x); maxX = Math.max(maxX, x);
        minY = Math.min(minY, y); maxY = Math.max(maxY, y);
      };
      placed.forEach((p) => {
        const co = Math.cos(p.ang), si = Math.sin(p.ang), bottom = p.h + 0.3 * u;
        [[-p.w * p.ax, 0], [p.w * (1 - p.ax), 0], [-p.w * p.ax, bottom], [p.w * (1 - p.ax), bottom]]
          .forEach(([a, b]) => grow(p.p.x + a * co - b * si, p.p.y + a * si + b * co));
      });
      if (info.gold) { grow(info.gold.minX, info.gold.minY); grow(info.gold.maxX, info.gold.maxY); }
      else info.ring.forEach((p) => grow(p.ox, p.oy));
      // marges pour les étiquettes en haut de l'aperçu et la mention en bas
      minY -= 45;
      maxY += 25;
      const ratio = RATIO[type];
      let cw = Math.max(420, maxX - minX + 70), ch = Math.max(cw * ratio, maxY - minY + 70);
      cw = Math.max(cw, ch / ratio);
      ch = cw * ratio;
      return { x: (minX + maxX) / 2 - cw / 2, y: (minY + maxY) / 2 - ch / 2, w: cw, h: ch };
    }

    async updateScene() {
      const id = (this.sceneId = (this.sceneId || 0) + 1);
      const type = this.state.type, chain = this.chain, items = this.state.items.slice();
      const H = Math.round(W * RATIO[type]);
      if (this.canvas.height !== H) {
        this.canvas.height = H;
        this.canvas.style.aspectRatio = `${W} / ${H}`;
        this.draw(this.ctx, performance.now());
      }
      const photo = await this.chainPhoto(chain, type);
      if (id !== this.sceneId) return;
      const pending = items.map((it) => this.cutout(this.charms.get(it.id)));
      // affiche tout de suite les charms déjà prêts, puis les autres dès qu'ils arrivent
      const place = () => {
        const { placed, u } = this.layout(type, photo.info, items, items.map((it) => this.ready.get(it.id) || null));
        const crop = this.cropFor(type, photo.info, placed, u);
        const sameChain = this.scene && this.scene.type === type && this.scene.chain === chain;
        this.scene = { type, chain, items, photo, placed, u, crop };
        if (!sameChain || !this.view) this.view = { ...crop };
        this.requestDraw();
      };
      place();
      pending.forEach((p) => p.then(() => { if (id === this.sceneId) place(); }));
      await Promise.all(pending);
      if (id !== this.sceneId) return;
      place();
      const names = items.map((it) => this.charms.get(it.id).t);
      this.canvas.setAttribute('aria-label', chain
        ? tr(`Aperçu : ${TYPE_LABEL[type].toLowerCase()} ${shortName(chain.t)}${names.length ? `, charms : ${names.join(', ')}` : ', sans charm'}`,
          `Preview: ${TYPE_EN[type].toLowerCase()} ${shortName(chain.t)}${names.length ? `, charms: ${names.join(', ')}` : ', no charms'}`)
        : tr('Aperçu du bijou', 'Jewellery preview'));
    }

    // Dessine une image, puis continue tant que le recadrage ou l'arrivée d'un charm est en cours
    requestDraw() {
      if (document.hidden || this.reduced) {
        if (this.scene) this.view = { ...this.scene.crop };
        this.draw(this.ctx, performance.now());
        return;
      }
      if (this.raf) return;
      const loop = (now) => {
        this.raf = null;
        if (!this.isConnected) return;
        this.draw(this.ctx, now);
        if (this.animating(now)) this.raf = requestAnimationFrame(loop);
      };
      this.raf = requestAnimationFrame(loop);
    }

    animating(now) {
      const sc = this.scene, v = this.view;
      if (!sc || !v) return false;
      const gap = Math.abs(v.x - sc.crop.x) + Math.abs(v.y - sc.crop.y) + Math.abs(v.w - sc.crop.w);
      return gap > 0.5 || (this.dropAt && now - this.dropAt < 600);
    }

    draw(ctx, now, still) {
      const H = ctx.canvas.height;
      ctx.setTransform(1, 0, 0, 1, 0, 0);
      const bg = ctx.createRadialGradient(W / 2, H * 0.35, 40, W / 2, H * 0.45, W * 0.75);
      bg.addColorStop(0, BG[0]);
      bg.addColorStop(1, BG[1]);
      ctx.fillStyle = bg;
      ctx.fillRect(0, 0, W, H);
      const sc = this.scene;
      if (!sc) return;

      // recadrage progressif vers le cadrage voulu
      let v = sc.crop;
      if (!still && this.view) {
        const t = this.reduced ? 1 : 0.2;
        ['x', 'y', 'w', 'h'].forEach((k) => { this.view[k] += (sc.crop[k] - this.view[k]) * t; });
        v = this.view;
      }
      const scale = W / v.w;
      ctx.setTransform(scale, 0, 0, scale, -v.x * scale, -v.y * scale);
      const { info, back } = sc.photo;
      if (back) {
        ctx.imageSmoothingQuality = 'high';
        ctx.drawImage(back.ext, -back.extOff, -back.extOff, back.extSize, back.extSize);
        ctx.drawImage(back.soft, 0, 0, A, A);
      } else {
        const path = new Path2D();
        info.ring.forEach((p, i) => (i ? path.lineTo(p.x, p.y) : path.moveTo(p.x, p.y)));
        path.closePath();
        strokeGold(ctx, path, 8, A, A);
      }

      ctx.shadowColor = 'rgba(40, 30, 20, 0.25)';
      ctx.shadowBlur = 8;
      ctx.shadowOffsetY = 4;
      const u = sc.u;
      sc.placed.forEach((p) => {
        if (!p.cut) return;
        let drop = 0;
        if (!still && !this.reduced && p.it.k === this.lastAdded && this.dropAt) {
          const q = Math.min(1, (now - this.dropAt) / 550);
          const back2 = 1 + 2.2 * Math.pow(q - 1, 3) + 1.2 * Math.pow(q - 1, 2);
          drop = -0.45 * u * (1 - back2);
        }
        ctx.save();
        ctx.translate(p.p.x, p.p.y);
        ctx.rotate(p.ang);
        ctx.translate(0, drop);
        drawRing(ctx, 0, 0.12 * u, 0.13 * u, 0.045 * u);
        if (p.cut.raw) ctx.globalCompositeOperation = 'multiply';
        ctx.drawImage(p.cut.canvas, -p.w * p.ax, 0.22 * u, p.w, p.h);
        ctx.restore();
      });
      ctx.setTransform(1, 0, 0, 1, 0, 0);
      ctx.shadowColor = 'transparent';

      if (!sc.items.length && !still) {
        const y = (info.cy - v.y) * scale;
        ctx.fillStyle = 'rgba(29, 26, 33, 0.5)';
        ctx.font = `italic 34px ${getComputedStyle(this).getPropertyValue('--cb-display') || 'Georgia, serif'}`;
        ctx.textAlign = 'center';
        ctx.fillText(tr('Vos charms apparaîtront ici', 'Your charms will appear here'), W / 2, y > 60 && y < H - 60 ? y : H / 2);
      }
    }

    /* ---------- Image atelier ---------- */

    async atelierImage(ref) {
      await this.updateScene();
      const sc = this.scene, chain = sc.chain;
      const H = Math.round(W * RATIO[sc.type]);
      const preview = document.createElement('canvas');
      preview.width = W;
      preview.height = H;
      this.draw(preview.getContext('2d'), 0, true);

      const n = sc.placed.length, cols = 6, tile = 150, gap = 24;
      const rows = Math.ceil(n / cols);
      const IW = 1200, previewH = Math.round(IW * (H / W));
      const IH = 110 + previewH + 70 + rows * (tile + 84) + 190;
      const c = document.createElement('canvas');
      c.width = IW;
      c.height = IH;
      const x = c.getContext('2d');
      const font = getComputedStyle(this).fontFamily;

      x.fillStyle = '#FFFFFF';
      x.fillRect(0, 0, IW, IH);
      x.fillStyle = '#1C1C1C';
      x.font = `600 34px ${font}`;
      x.fillText(`DE NADA PARIS · ${TYPE_LABEL[sc.type]} à charms`, 50, 64);
      x.font = `24px ${font}`;
      x.fillStyle = '#6B6B6B';
      x.textAlign = 'right';
      x.fillText(`Réf. ${ref} · ${new Date().toLocaleDateString('fr-FR')}`, IW - 50, 64);
      x.textAlign = 'left';
      x.drawImage(preview, 0, 100, IW, previewH);

      let y = 100 + previewH + 56;
      x.fillStyle = '#1C1C1C';
      x.font = `600 26px ${font}`;
      x.fillText("Ordre d'accrochage (de gauche à droite, comme sur l'aperçu)", 50, y);
      y += 24;
      const rowW = Math.min(cols, n) * (tile + gap) - gap;
      sc.placed.forEach((p, i) => {
        const col = i % cols, row = Math.floor(i / cols);
        const tx = (IW - rowW) / 2 + col * (tile + gap), ty = y + row * (tile + 84);
        x.strokeStyle = '#DDDDDD';
        x.lineWidth = 2;
        x.strokeRect(tx, ty + 34, tile, tile);
        x.fillStyle = '#1C1C1C';
        x.font = `600 24px ${font}`;
        x.textAlign = 'center';
        x.fillText(String(i + 1), tx + tile / 2, ty + 26);
        if (p.cut) {
          const k = Math.min((tile - 20) / p.cut.w, (tile - 20) / p.cut.h);
          const w = p.cut.w * k, h = p.cut.h * k;
          x.drawImage(p.cut.canvas, tx + (tile - w) / 2, ty + 34 + (tile - h) / 2, w, h);
        }
        x.font = `18px ${font}`;
        x.fillStyle = '#6B6B6B';
        const label = this.charms.get(p.it.id).t;
        x.fillText(label.length > 18 ? `${label.slice(0, 17)}…` : label, tx + tile / 2, ty + 34 + tile + 26);
        x.textAlign = 'left';
      });

      y += rows * (tile + 84) + 50;
      x.fillStyle = '#1C1C1C';
      x.font = `26px ${font}`;
      x.fillText(`Chaîne : ${chain.t.replace(/\u00a0/g, ' ')}${chain.size ? ` (${chain.size})` : ''}   ·   ${n} charm${n > 1 ? 's' : ''}`, 50, y);
      if (this.state.note) {
        const note = this.state.note.replace(/\s+/g, ' ');
        x.fillText(`Note : ${note.length > 80 ? `${note.slice(0, 79)}…` : note}`, 50, y + 44);
      }
      return new Promise((resolve) => c.toBlob(resolve, 'image/png'));
    }

    /* ---------- Panier ---------- */

    async refreshCart() {
      try {
        const root = window.Shopify?.routes?.root || '/';
        const cart = await (await fetch(`${root}cart.js`)).json();
        this.cartTotal = cart.total_price || 0;
        this.renderReceipt();
      } catch (e) { /* hors boutique */ }
    }

    async addToCart() {
      const s = this.state, chain = this.chain;
      if (!chain || !chain.ok) { this.go(0); this.showError(tr('Choisissez une chaîne disponible.', 'Choose an available chain.')); return; }
      if (!s.items.length) { this.go(1); this.showError(tr('Ajoutez au moins un charm à votre création.', 'Add at least one charm to your creation.')); return; }
      if (this.busy) return;
      const root = window.Shopify?.routes?.root || '/';
      const ref = s.ref;
      const names = s.items.map((it) => this.charms.get(it.id).t);
      this.busy = true;
      this.renderBar();
      let addedKeys = [];
      try {
        // 1. Les charms, en lignes séparées pour le suivi du stock
        const counts = new Map();
        s.items.forEach((it) => counts.set(it.id, (counts.get(it.id) || 0) + 1));
        const items = [...counts].map(([id, q]) => ({ id: this.charms.get(id).v, quantity: q, properties: { _Bijou: ref } }));
        let res = await fetch(`${root}cart/add.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
          body: JSON.stringify({ items })
        });
        let json = await res.json();
        if (!res.ok) throw new Error(json.description || json.message || tr('Ajout impossible.', 'Could not add to cart.'));
        addedKeys = (json.items || []).map((i) => i.key);

        // 2. La chaîne, avec la composition et l'image atelier
        const sections = [];
        document.documentElement.dispatchEvent(new CustomEvent('cart:prepare-bundled-sections', { bubbles: true, detail: { sections } }));
        const blob = await this.atelierImage(ref).catch(() => null);
        const body = (withImage) => {
          const fd = new FormData();
          fd.append('id', chain.v);
          fd.append('quantity', '1');
          fd.append('properties[Charms]', names.map((n, i) => `${i + 1}. ${n}`).join(' · '));
          fd.append('properties[Nombre de charms]', String(names.length));
          if (s.note.trim()) fd.append('properties[Informations complémentaires]', s.note.trim());
          fd.append('properties[_Réf]', ref);
          // Préfixe « _ » : invisible pour le client, visible dans l'admin de la commande
          if (withImage && blob) fd.append('properties[_Aperçu atelier]', blob, `bijou-${ref}.png`);
          if (sections.length) {
            fd.append('sections', sections.join(','));
            fd.append('sections_url', location.pathname);
          }
          return fd;
        };
        res = await fetch(`${root}cart/add.js`, { method: 'POST', body: body(true), headers: { 'X-Requested-With': 'XMLHttpRequest' } });
        json = await res.json();
        if (!res.ok && blob) {
          // Si l'envoi de l'image échoue, la composition texte suffit à l'atelier
          res = await fetch(`${root}cart/add.js`, { method: 'POST', body: body(false), headers: { 'X-Requested-With': 'XMLHttpRequest' } });
          json = await res.json();
        }
        if (!res.ok) throw new Error(json.description || json.message || tr('Ajout impossible.', 'Could not add to cart.'));
        addedKeys = [];

        // Création suivante : même chaîne, sans charms
        s.items = [];
        s.note = '';
        s.ref = randomRef();
        s.step = 1;
        this.lastAdded = null;
        this.save();

        if (window.themeVariables?.settings?.cartType === 'page') {
          window.location.href = `${root}cart`;
          return;
        }
        const cart = await (await fetch(`${root}cart.js`)).json();
        this.cartTotal = cart.total_price || 0;
        if (json.sections) {
          cart.sections = json.sections;
          document.documentElement.dispatchEvent(new CustomEvent('cart:change', {
            bubbles: true,
            detail: { baseEvent: 'variant:add', onSuccessDo: 'force_open_drawer', cart }
          }));
        } else {
          document.dispatchEvent(new CustomEvent('cart:refresh'));
        }
        this.toast(tr('Votre création est dans le panier.', 'Your creation is in the cart.'));
      } catch (err) {
        // Retire les charms déjà ajoutés si la chaîne n'a pas pu l'être
        const root2 = window.Shopify?.routes?.root || '/';
        for (const key of addedKeys) {
          await fetch(`${root2}cart/change.js`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ id: key, quantity: 0 })
          }).catch(() => {});
        }
        this.showError(err.message || tr('Ajout au panier impossible. Réessayez.', 'Could not add to cart. Please try again.'));
      } finally {
        this.busy = false;
        this.render();
      }
    }
  }

  customElements.define('charms-builder', CharmsBuilder);
})();
